import { DeleteOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import {
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Row,
    Space,
    Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WebGrid, { type WebGridColumn } from '../../../webgrid/src';
import {
    buildNewPenaltyPayload,
    buildPenaltyFlagUpdate,
    calculatePenaltyTotals,
    type CashHandover,
    createEmptyPenaltyForm,
    type DateRange,
    formatDay,
    formatLeiWhole,
    getDefaultHandoverRange,
    type PenaltyFlag,
    type PenaltyForm,
} from '../../domain/controlling';
import type { UserPenalty } from '../../domain/salary/userPenalties';
import { withCurrency } from '../../domain/controlRoom';
import useRequest from '../../hooks/useRequest';

const { Text } = Typography;

const DATE_FORMAT = 'YYYY-MM-DD';
const SELECTED_ROW_STYLE = { backgroundColor: '#e6f4ff' };
const GRID_HEIGHT = 'calc(100vh - 380px)';

interface CashHandoversResponse {
    success: boolean;
    data: { handovers: CashHandover[] };
}

const renderLei = (value: unknown) => formatLeiWhole(value as number | null);
const renderDay = (value: unknown) => formatDay(value as string | null);

/**
 * "Predari casa" (legacy `tsRapoarteCasa`): the cash handovers of a period and,
 * for the handover picked in the grid, the penalties of its user in that period
 * with their "to apply" and "applied" totals.
 */
const CashHandoversPanel = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const [range, setRange] = useState<DateRange>(() => getDefaultHandoverRange(new Date()));
    const [handovers, setHandovers] = useState<CashHandover[]>([]);
    const [handoversLoading, setHandoversLoading] = useState(false);
    const [selectedHandover, setSelectedHandover] = useState<CashHandover | null>(null);
    const [penalties, setPenalties] = useState<UserPenalty[]>([]);
    const [penaltiesLoading, setPenaltiesLoading] = useState(false);
    const [selectedPenaltyId, setSelectedPenaltyId] = useState<number | null>(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [penaltyForm, setPenaltyForm] = useState<PenaltyForm>(() =>
        createEmptyPenaltyForm(new Date()),
    );
    const [penaltySaving, setPenaltySaving] = useState(false);

    const loadPenalties = useCallback(
        async (userId: number | null, period: DateRange) => {
            if (userId === null) {
                setPenalties([]);
                return;
            }
            setPenaltiesLoading(true);
            try {
                const data = await request<UserPenalty[]>({
                    path: '/user-penalties',
                    params: { userId, startDate: period.startDate, endDate: period.endDate },
                });
                setPenalties(data);
            } catch (error) {
                message.error(t('controlRoom.controlling.penaltiesLoadError'));
                console.error('Failed to load user penalties', error);
            } finally {
                setPenaltiesLoading(false);
            }
        },
        [request, t],
    );

    const loadHandovers = useCallback(
        async (period: DateRange) => {
            setHandoversLoading(true);
            try {
                // Handovers short by more than the tolerance become penalties first, as the
                // legacy tab did when it was shown.
                await request({
                    path: '/user-penalties/sync',
                    method: 'POST',
                    params: { startDate: period.startDate, endDate: period.endDate },
                }).catch((error: unknown) => console.warn('Sync user penalties failed', error));

                const result = await request<CashHandoversResponse>({
                    path: '/controlling/cash-handovers',
                    params: { startDate: period.startDate, endDate: period.endDate },
                });
                setHandovers(result.data.handovers);
            } catch (error) {
                message.error(t('controlRoom.controlling.handoversLoadError'));
                console.error('Failed to load cash handovers', error);
            } finally {
                setHandoversLoading(false);
            }
        },
        [request, t],
    );

    // Load the default period once; later period changes reload explicitly in changeRange.
    const initialRange = useRef(range);
    useEffect(() => {
        void loadHandovers(initialRange.current);
    }, [loadHandovers]);

    const changeRange = (nextRange: DateRange) => {
        setRange(nextRange);
        void loadHandovers(nextRange);
        void loadPenalties(selectedHandover?.userId ?? null, nextRange);
    };

    const selectHandover = (handover: CashHandover) => {
        setSelectedHandover(handover);
        setSelectedPenaltyId(null);
        void loadPenalties(handover.userId, range);
    };

    const refreshPenalties = () => loadPenalties(selectedHandover?.userId ?? null, range);

    const togglePenaltyFlag = useCallback(
        async (penalty: UserPenalty, flag: PenaltyFlag, checked: boolean) => {
            const update = buildPenaltyFlagUpdate(penalty.id, flag, checked, new Date());
            // Tick the box at once; the reload below brings back what was actually stored.
            setPenalties(previous =>
                previous.map(row => (row.id === penalty.id ? { ...row, ...update } : row)),
            );
            try {
                await request({ path: '/user-penalties', method: 'POST', params: { ...update } });
            } catch (error) {
                message.error(t('controlRoom.controlling.penaltySaveError'));
                console.error('Failed to update user penalty', error);
            }
            await loadPenalties(penalty.userId, range);
        },
        [request, t, loadPenalties, range],
    );

    const openAddModal = () => {
        setPenaltyForm(createEmptyPenaltyForm(new Date()));
        setAddModalOpen(true);
    };

    const saveNewPenalty = async () => {
        if (!selectedHandover) {
            return;
        }
        const payload = buildNewPenaltyPayload(selectedHandover, penaltyForm);
        if (!payload) {
            message.warning(t('controlRoom.controlling.penaltyValueRequired'));
            return;
        }
        setPenaltySaving(true);
        try {
            await request({ path: '/user-penalties', method: 'POST', params: { ...payload } });
            message.success(t('controlRoom.controlling.penaltySaved'));
            setAddModalOpen(false);
            await refreshPenalties();
        } catch (error) {
            message.error(t('controlRoom.controlling.penaltySaveError'));
            console.error('Failed to add user penalty', error);
        } finally {
            setPenaltySaving(false);
        }
    };

    const deleteSelectedPenalty = async () => {
        if (selectedPenaltyId === null) {
            return;
        }
        try {
            await request({ path: `/user-penalties/${selectedPenaltyId}`, method: 'DELETE' });
            message.success(t('controlRoom.controlling.penaltyDeleted'));
            setSelectedPenaltyId(null);
            await refreshPenalties();
        } catch (error) {
            message.error(t('controlRoom.controlling.penaltyDeleteError'));
            console.error('Failed to delete user penalty', error);
        }
    };

    const handoverColumns = useMemo<WebGridColumn<CashHandover>[]>(
        () => [
            {
                key: 'handoverDate',
                title: t('controlRoom.controlling.date'),
                dataIndex: 'handoverDate',
                sortable: true,
                filterable: true,
            },
            {
                key: 'handoverTime',
                title: t('controlRoom.controlling.time'),
                dataIndex: 'handoverTime',
            },
            {
                key: 'amount',
                title: withCurrency(t('controlRoom.controlling.amount'), 'LEI'),
                dataIndex: 'amount',
                align: 'right',
                render: renderLei,
            },
            {
                key: 'notes',
                title: t('controlRoom.controlling.notes'),
                dataIndex: 'notes',
                filterable: true,
            },
            {
                key: 'difference',
                title: withCurrency(t('controlRoom.controlling.difference'), 'LEI'),
                dataIndex: 'difference',
                align: 'right',
                sortable: true,
                render: renderLei,
            },
            {
                key: 'username',
                title: t('controlRoom.controlling.user'),
                dataIndex: 'username',
                sortable: true,
                filterable: true,
            },
            {
                key: 'isEdited',
                title: t('controlRoom.controlling.edited'),
                dataIndex: 'isEdited',
                align: 'center',
                render: (value: unknown) => <Checkbox checked={Boolean(value)} disabled />,
            },
        ],
        [t],
    );

    const penaltyColumns = useMemo<WebGridColumn<UserPenalty>[]>(
        () => [
            { key: 'id', title: t('controlRoom.controlling.id'), dataIndex: 'id' },
            {
                key: 'utilizator',
                title: t('controlRoom.controlling.user'),
                dataIndex: 'utilizator',
            },
            {
                key: 'diff',
                title: withCurrency(t('controlRoom.controlling.difference'), 'LEI'),
                dataIndex: 'diff',
                align: 'right',
                render: renderLei,
            },
            {
                key: 'mentiuni',
                title: t('controlRoom.controlling.description'),
                dataIndex: 'mentiuni',
                filterable: true,
            },
            {
                key: 'seAplica',
                title: t('controlRoom.controlling.applies'),
                dataIndex: 'seAplica',
                align: 'center',
                render: (value: unknown, penalty: UserPenalty) => (
                    <Checkbox
                        aria-label={t('controlRoom.controlling.applies')}
                        checked={Boolean(value)}
                        onChange={event =>
                            togglePenaltyFlag(penalty, 'seAplica', event.target.checked)
                        }
                    />
                ),
            },
            {
                key: 'dataAbatere',
                title: t('controlRoom.controlling.offenceDate'),
                dataIndex: 'dataAbatere',
                sortable: true,
                render: renderDay,
            },
            {
                key: 'aplicat',
                title: t('controlRoom.controlling.applied'),
                dataIndex: 'aplicat',
                align: 'center',
                render: (value: unknown, penalty: UserPenalty) => (
                    <Checkbox
                        aria-label={t('controlRoom.controlling.applied')}
                        checked={Boolean(value)}
                        onChange={event =>
                            togglePenaltyFlag(penalty, 'aplicat', event.target.checked)
                        }
                    />
                ),
            },
            {
                key: 'dataAplicare',
                title: t('controlRoom.controlling.appliedDate'),
                dataIndex: 'dataAplicare',
                render: renderDay,
            },
        ],
        [t, togglePenaltyFlag],
    );

    const totals = calculatePenaltyTotals(penalties);
    const canAddPenalty = selectedHandover?.userId != null;

    return (
        <Space
            direction="vertical"
            size="middle"
            style={{ width: '100%' }}
            data-testid="controlling-cash-handovers"
        >
            <Space wrap>
                <Text>{t('controlRoom.controlling.from')}</Text>
                <DatePicker
                    aria-label={t('controlRoom.controlling.from')}
                    value={dayjs(range.startDate)}
                    format={DATE_FORMAT}
                    allowClear={false}
                    onChange={value => {
                        if (value) {
                            changeRange({ ...range, startDate: value.format(DATE_FORMAT) });
                        }
                    }}
                />
                <Text>{t('controlRoom.controlling.to')}</Text>
                <DatePicker
                    aria-label={t('controlRoom.controlling.to')}
                    value={dayjs(range.endDate)}
                    format={DATE_FORMAT}
                    allowClear={false}
                    onChange={value => {
                        if (value) {
                            changeRange({ ...range, endDate: value.format(DATE_FORMAT) });
                        }
                    }}
                />
                <Button type="primary" onClick={() => changeRange(range)}>
                    {t('controlRoom.controlling.show')}
                </Button>
            </Space>

            <Row gutter={[16, 16]}>
                <Col xs={24} xl={11}>
                    <Card
                        size="small"
                        title={t('controlRoom.controlling.handoversTitle')}
                        data-testid="controlling-handovers-card"
                    >
                        <WebGrid<CashHandover>
                            data={handovers}
                            columns={handoverColumns}
                            loading={handoversLoading}
                            filterBar
                            pagination
                            pageSize={50}
                            onRowClick={selectHandover}
                            getRowStyle={handover =>
                                handover.id === selectedHandover?.id
                                    ? SELECTED_ROW_STYLE
                                    : undefined
                            }
                            rowIdAccessor={handover => handover.id}
                            height={GRID_HEIGHT}
                            emptyMessage={t('controlRoom.controlling.noHandovers')}
                        />
                    </Card>
                </Col>
                <Col xs={24} xl={13}>
                    <Card
                        size="small"
                        data-testid="controlling-penalties-card"
                        title={
                            selectedHandover
                                ? t('controlRoom.controlling.penaltiesTitle', {
                                      user: selectedHandover.username ?? '-',
                                  })
                                : t('controlRoom.controlling.penaltiesHint')
                        }
                    >
                        {selectedHandover && (
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Space wrap>
                                    <Button
                                        danger
                                        icon={<PlusOutlined />}
                                        disabled={!canAddPenalty}
                                        onClick={openAddModal}
                                    >
                                        {t('controlRoom.controlling.addPenalty')}
                                    </Button>
                                    <Popconfirm
                                        title={t('controlRoom.controlling.confirmDeletePenalty')}
                                        onConfirm={deleteSelectedPenalty}
                                        disabled={selectedPenaltyId === null}
                                    >
                                        <Button
                                            icon={<DeleteOutlined />}
                                            disabled={selectedPenaltyId === null}
                                        >
                                            {t('controlRoom.controlling.deletePenalty')}
                                        </Button>
                                    </Popconfirm>
                                    <Button icon={<SyncOutlined />} onClick={refreshPenalties}>
                                        {t('controlRoom.controlling.refreshPenalties')}
                                    </Button>
                                </Space>
                                <WebGrid<UserPenalty>
                                    data={penalties}
                                    columns={penaltyColumns}
                                    loading={penaltiesLoading}
                                    filterBar
                                    onRowClick={penalty => setSelectedPenaltyId(penalty.id)}
                                    getRowStyle={penalty =>
                                        penalty.id === selectedPenaltyId
                                            ? SELECTED_ROW_STYLE
                                            : undefined
                                    }
                                    rowIdAccessor={penalty => penalty.id}
                                    height="calc(100vh - 480px)"
                                    emptyMessage={t('controlRoom.controlling.noPenalties')}
                                />
                                <Text type="secondary">
                                    {t('controlRoom.controlling.inSelectedPeriod')}
                                </Text>
                                <Space size="large" wrap>
                                    <Text strong data-testid="controlling-total-to-apply">
                                        {withCurrency(
                                            t('controlRoom.controlling.totalToApply'),
                                            'LEI',
                                        )}
                                        : {formatLeiWhole(totals.toApply)}
                                    </Text>
                                    <Text strong data-testid="controlling-total-applied">
                                        {withCurrency(
                                            t('controlRoom.controlling.totalApplied'),
                                            'LEI',
                                        )}
                                        : {formatLeiWhole(totals.applied)}
                                    </Text>
                                </Space>
                            </Space>
                        )}
                    </Card>
                </Col>
            </Row>

            <Modal
                title={t('controlRoom.controlling.addPenaltyTitle')}
                open={addModalOpen}
                onOk={saveNewPenalty}
                onCancel={() => setAddModalOpen(false)}
                confirmLoading={penaltySaving}
                okText={t('controlRoom.controlling.save')}
                cancelText={t('common.cancel')}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>{withCurrency(t('controlRoom.controlling.penaltyValue'), 'LEI')}</Text>
                    <InputNumber
                        aria-label={t('controlRoom.controlling.penaltyValue')}
                        value={penaltyForm.value}
                        onChange={value => setPenaltyForm({ ...penaltyForm, value })}
                        precision={2}
                        style={{ width: '100%' }}
                    />
                    <Text>{t('controlRoom.controlling.description')}</Text>
                    <Input
                        aria-label={t('controlRoom.controlling.description')}
                        value={penaltyForm.description}
                        maxLength={200}
                        onChange={event =>
                            setPenaltyForm({ ...penaltyForm, description: event.target.value })
                        }
                    />
                    <Text>{t('controlRoom.controlling.offenceDate')}</Text>
                    <DatePicker
                        aria-label={t('controlRoom.controlling.offenceDate')}
                        value={penaltyForm.date ? dayjs(penaltyForm.date) : null}
                        format={DATE_FORMAT}
                        style={{ width: '100%' }}
                        onChange={value =>
                            setPenaltyForm({
                                ...penaltyForm,
                                date: value ? value.format(DATE_FORMAT) : '',
                            })
                        }
                    />
                </Space>
            </Modal>
        </Space>
    );
};

export default CashHandoversPanel;
